import Icon from "@mdi/react";
import {
  mdiAlertOutline,
  mdiBlockHelper,
  mdiChevronDown,
  mdiChevronRight,
  mdiEyeOutline,
  mdiFileCheckOutline,
  mdiHistory,
  mdiRotateLeft,
  mdiSwapHorizontal,
} from "@mdi/js";
import React, { useEffect, useMemo, useState } from "react";
import InwardViewModal from "./InwardViewModal";
import CurrencyBadge from "./CurrencyBadge";
import {
  formatAmountWithCommas,
  formatDateYYYYMMDD,
} from "../../content_panel/utils/helpers";
import { WarehouseInwardModel } from "../model/warehouse_inward_models";
import { useWarehouseInward } from "../contexts/InwardContext";
import { useHistory } from "react-router-dom";
import { urls } from "../../../../utils/urls";
import { useWarehouseViewOnly } from "../hooks/useWarehouseViewOnly";
import PaginationInfo from "../common-components/PaginationInfo";
import { MobileCard, MobileField } from "../common-components/MobileCard";
import { History } from "lucide-react";
import WorkflowTimelineRow from "../workflow/WorkflowTimelineRow";
import {
  getWorkflowTimelinesBatch,
  WorkflowEvent,
} from "../workflow/workflow_repo";

const statusClassMap: Record<string, string> = {
  received: "status-received",
  partial: "status-partial",
  pending: "status-pending",
  disputed: "status-disputed",
};

interface InwardOrdersTableProps {
  updatesAllowed: boolean;
  cascade?: boolean;
  cascadeHistory?: boolean;
}

const InwardOrdersTable: React.FC<InwardOrdersTableProps> = ({
  updatesAllowed,
  cascade = false,
  cascadeHistory = false,
}) => {
  const {
    inwardList,
    selectedInward,
    isViewModalOpen,
    openViewModal,
    closeModals,
    filters,
    setFilters,
    page,
    pagination,
  } = useWarehouseInward();
  const isWarehouseViewOnly = useWarehouseViewOnly();

  const itemSearchTokens = (filters.itemSearch || "")
    .split(",")
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
  const isItemMatch = (it: { itemName?: string; inventoryItemCode?: string }) =>
    itemSearchTokens.length > 0 &&
    itemSearchTokens.some(
      (tok) =>
        (it.itemName || "").toLowerCase().includes(tok) ||
        (it.inventoryItemCode || "").toLowerCase().includes(tok),
    );

  const sortBy = filters.sortBy;
  const sortOrder = filters.sortOrder;
  const cycleSortByValue = () => {
    setFilters((p) => {
      if (p.sortBy !== "value")
        return { ...p, sortBy: "value", sortOrder: "asc" };
      if (p.sortOrder === "asc")
        return { ...p, sortBy: "value", sortOrder: "desc" };
      return { ...p, sortBy: undefined, sortOrder: undefined };
    });
  };
  const sortArrow =
    sortBy === "value" ? (sortOrder === "asc" ? "▲" : "▼") : "↕";

  const disputeSummary = useMemo(() => {
    const map: Record<
      string,
      { hasOpenDispute: boolean; quarantinedCount: number }
    > = {};

    inwardList.forEach((row) => {
      const openDisputes = (row.past_disputes || []).filter(
        (d) => d.status?.toLowerCase() === "open",
      );
      const quarantinedCount = openDisputes.reduce(
        (acc, curr) => acc + (curr.disputed_physical_quantity || 0),
        0,
      );

      map[row.inward_order_id] = {
        hasOpenDispute: openDisputes.length > 0 || row.is_disputed === true,
        quarantinedCount,
      };
    });

    return map;
  }, [inwardList]);

  const navigate = useHistory();

  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [timelineRows, setTimelineRows] = useState<Set<string>>(new Set());

  useEffect(() => {
    setExpandedRows(new Set());
  }, [cascade]);

  useEffect(() => {
    setTimelineRows(new Set());
  }, [cascadeHistory]);

  const [timelinesByEntity, setTimelinesByEntity] = useState<
    Record<string, WorkflowEvent[]>
  >({});
  const [historyBatchLoading, setHistoryBatchLoading] = useState(false);

  const visibleEntityIds = useMemo(
    () => (inwardList ?? []).map((r) => r.inward_order_id).filter(Boolean),
    [inwardList],
  );

  useEffect(() => {
    setTimelinesByEntity({});
  }, [visibleEntityIds.join("|")]);

  // Batch-fetch all visible rows' timelines in one HTTP call when cascadeHistory
  // turns on, but only for IDs not already cached. Also covers manually-toggled
  // rows (not just cascadeHistory ones) — the desktop table and mobile card are
  // both always mounted (CSS-toggled, not conditionally rendered), so without
  // this a manually-opened row's WorkflowTimelineRow mounts twice and, lacking
  // prefetchedEvents, each instance fires its own duplicate fetch.
  useEffect(() => {
    const neededIds = cascadeHistory
      ? visibleEntityIds
      : (inwardList ?? [])
          .filter((row) => timelineRows.has(row.id))
          .map((r) => r.inward_order_id)
          .filter(Boolean);
    const missing = neededIds.filter((id) => !(id in timelinesByEntity));
    if (!missing.length) return;
    let cancelled = false;
    setHistoryBatchLoading(true);
    getWorkflowTimelinesBatch(missing).then((res) => {
      if (cancelled) return;
      if (res?.success && res.data) {
        setTimelinesByEntity((prev) => ({ ...prev, ...res.data! }));
      }
      setHistoryBatchLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [
    cascadeHistory,
    visibleEntityIds,
    timelinesByEntity,
    timelineRows,
    inwardList,
  ]);

  const toggleExpanded = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleTimeline = (id: string) => {
    setTimelineRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleRowClick = (row: WarehouseInwardModel) => {
    const status = row.status?.toLowerCase();
    const hasOpenDispute =
      disputeSummary[row.inward_order_id]?.hasOpenDispute ?? false;
    if (hasOpenDispute) {
      openViewModal(row);
      return;
    }
    if (
      updatesAllowed &&
      !isWarehouseViewOnly &&
      (status === "pending" || status === "partial")
    ) {
      row?.poType === "Infra PO"
        ? navigate.push(
            `${urls.warehouse_inward}/receive/${row?.type}/${row?.poType}/${row?.inward_order_id}`,
          )
        : navigate.push(
            `${urls.warehouse_inward}/receive/${row?.type}/${row?.inward_order_id}`,
          );
    } else if (status === "received") {
      openViewModal(row);
    }
  };

  return (
    <>
      <div className="rounded-lg border border-gray-300 shadow-sm bg-white">
        <div className="p-6 pb-3 flex items-center justify-between">
          <h3 className="text-base font-semibold">Inward Orders</h3>
          <PaginationInfo
            totalRecords={pagination.total}
            currentPage={page}
            limit={pagination.limit}
          />
        </div>

        <div className="p-6 pt-0 overflow-x-auto hidden md:block">
          <table
            id="warehouse-inward-orders-table"
            className="w-full min-w-max text-sm gray"
          >
            <thead className="border-b top-0 bg-white">
              <tr>
                <th className="p-3 pr-5 text-left text-muted-foreground whitespace-nowrap left-0 bg-white">
                  Order ID
                </th>
                <th className="p-3 text-left text-muted-foreground whitespace-nowrap"></th>
                <th className="p-3 text-left text-muted-foreground whitespace-nowrap">
                  Type
                </th>
                <th className="p-3 text-left text-muted-foreground whitespace-nowrap hidden md:table-cell">
                  Created By
                </th>
                <th className="p-3 text-left text-muted-foreground whitespace-nowrap hidden md:table-cell">
                  Created At
                </th>
                <th className="p-3 text-left text-muted-foreground whitespace-nowrap hidden md:table-cell">
                  From
                </th>
                <th className="p-3 text-left text-muted-foreground whitespace-nowrap hidden md:table-cell">
                  To Warehouse
                </th>
                <th className="p-3 text-left text-muted-foreground whitespace-nowrap hidden md:table-cell">
                  Expected
                </th>
                <th className="p-3 text-left text-muted-foreground whitespace-nowrap hidden md:table-cell">
                  <button
                    type="button"
                    id="warehouse-inward-sort-value-btn"
                    onClick={cycleSortByValue}
                    className="inline-flex items-center gap-1 hover:text-gray-800"
                  >
                    Value
                    <span
                      className={
                        sortBy === "value" ? "text-blue-600" : "text-gray-400"
                      }
                    >
                      {sortArrow}
                    </span>
                  </button>
                </th>
                <th className="p-3 text-left text-muted-foreground whitespace-nowrap">
                  Status
                </th>
                <th className="p-3 text-right text-muted-foreground whitespace-nowrap">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {inwardList &&
                inwardList.length > 0 &&
                inwardList.map((row: WarehouseInwardModel) => {
                  const statusKey = row?.status.toLowerCase();
                  const statusClass = statusClassMap[statusKey] ?? "";

                  const isRevised =
                    row.inward_order_id?.toLowerCase()?.startsWith("po-") &&
                    row.inward_order_id?.split("-")?.length > 2;

                  const openDisputeSummary = disputeSummary[
                    row.inward_order_id
                  ] ?? {
                    hasOpenDispute: false,
                    quarantinedCount: 0,
                  };

                  const hasOpenDispute = openDisputeSummary.hasOpenDispute;
                  const quarantinedCount = openDisputeSummary.quarantinedCount;

                  const items = row.items || [];
                  const hasItems = items.length > 0;
                  const hasMatchedItem = items.some(isItemMatch);
                  const isExpanded =
                    expandedRows.has(row.id) || hasMatchedItem || cascade;
                  const rowId = row.inward_order_id ?? row.id;

                  return (
                    <React.Fragment key={row.id}>
                      <tr
                        id={`warehouse-inward-row-${rowId}`}
                        onClick={() => handleRowClick(row)}
                        className="border-b hover:bg-gray-50 cursor-pointer"
                      >
                        <td
                          className={`p-3 pr-5 font-semibold whitespace-nowrap sticky left-0 bg-white flex flex-row items-center gap-2 ${
                            isRevised ? "border-l-4 border-l-primary" : ""
                          }`}
                        >
                          <div className="relative group">
                            <button
                              id={`warehouse-inward-row-${rowId}-timeline`}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleTimeline(row.id);
                              }}
                              className={`h-6 w-6 inline-flex items-center justify-center rounded hover:bg-gray-100 ${
                                timelineRows.has(row.id)
                                  ? "text-blue-600"
                                  : "text-gray-500"
                              }`}
                              aria-label="Workflow timeline"
                            >
                              <History className="h-3.5 w-3.5" />
                            </button>
                            <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1 rounded-md bg-gray-700 px-1.5 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100 whitespace-nowrap z-50">
                              Workflow timeline
                            </span>
                          </div>
                          {hasItems ? (
                            <button
                              id={`warehouse-inward-row-${rowId}-expand`}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleExpanded(row.id);
                              }}
                              className="h-6 w-6 inline-flex items-center justify-center rounded hover:bg-gray-100 text-gray-500"
                              aria-label={
                                isExpanded ? "Collapse items" : "Expand items"
                              }
                            >
                              <Icon
                                path={
                                  isExpanded ? mdiChevronDown : mdiChevronRight
                                }
                                size={0.7}
                              />
                            </button>
                          ) : (
                            <span className="inline-block h-6 w-6" />
                          )}
                          {row?.inward_order_id ?? "--"}
                        </td>

                        <td className="p-3 font-semibold whitespace-nowrap">
                          <div className="flex flex-col gap-2">
                            {isRevised && (
                              <div className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold gap-1 status-revised">
                                <Icon path={mdiHistory} size={0.5} />
                                Revise
                              </div>
                            )}

                            {row.is_disputed && (
                              <div className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold gap-1 status-rejected">
                                <Icon path={mdiBlockHelper} size={0.4} />
                                GRN Blocked
                              </div>
                            )}

                            {hasOpenDispute && (
                              <div className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold gap-1 status-disputed">
                                <Icon path={mdiAlertOutline} size={0.5} />
                                Disputed
                              </div>
                            )}

                            {quarantinedCount > 0 && (
                              <div className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold gap-1 status-pending">
                                <Icon path={mdiAlertOutline} size={0.5} />
                                {quarantinedCount} quarantined
                              </div>
                            )}
                          </div>
                        </td>

                        <td className="p-3 font-semibold whitespace-nowrap flex flex-col gap-1">
                          {row?.type}
                          {row?.st_subtype &&
                            row?.type === "ST" &&
                            ["Replacement", "Returnable"].includes(
                              row?.st_subtype,
                            ) && (
                              <div
                                className={` items-center rounded-full border gap-2 font-medium text-xs px-1 py-0.5 w-fit hidden sm:inline-flex ${
                                  row.st_subtype === "Replacement"
                                    ? "bg-yellow-100 text-yellow-600 border-yellow-300"
                                    : "bg-blue-100 text-blue-600 border-blue-300"
                                }`}
                              >
                                <Icon
                                  path={
                                    row.st_subtype === "Replacement"
                                      ? mdiSwapHorizontal
                                      : mdiRotateLeft
                                  }
                                  size={0.5}
                                />
                                {row.st_subtype}
                              </div>
                            )}
                        </td>

                        <td className="p-3 text-muted-foreground whitespace-nowrap  hidden md:table-cell">
                          {row?.created_by_name}
                        </td>

                        <td className="p-3 text-muted-foreground whitespace-nowrap hidden md:table-cell">
                          {formatDateYYYYMMDD(row?.created_at)}
                        </td>

                        <td className="p-3 text-muted-foreground whitespace-nowrap hidden md:table-cell">
                          {row?.from?.name}
                        </td>

                        <td className="p-3 lg:table-cell text-muted-foreground whitespace-nowrap hidden md:table-cell">
                          {row.toWarehouse?.name}
                        </td>

                        <td className="p-3 text-muted-foreground whitespace-nowrap hidden md:table-cell ">
                          {formatDateYYYYMMDD(row?.expectedDate)}
                        </td>

                        <td className="p-3 text-left font-semibold whitespace-nowrap hidden md:table-cell">
                          ₹ {formatAmountWithCommas(row?.totalPrice)}
                          <CurrencyBadge
                            currency={row?.currency}
                            rate={row?.vendor_currency_to_inr}
                            inrAmount={row?.totalPrice}
                          />
                        </td>

                        <td className="p-3 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${statusClass}`}
                          >
                            {row.status}
                          </span>
                        </td>

                        <td className="p-3 text-right whitespace-nowrap flex gap-1 justify-end">
                          <div className="relative group">
                            <button
                              id={`warehouse-inward-row-${rowId}-action-view`}
                              onClick={(e) => {
                                e.stopPropagation();
                                openViewModal(row);
                              }}
                              className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-yellow-500"
                            >
                              <Icon
                                path={mdiEyeOutline}
                                className="h-4 w-4 text-muted-foreground group-hover:text-white"
                              />
                            </button>

                            <span
                              className="
                              pointer-events-none
                              absolute top-full left-1/2 -translate-x-1/2 mt-1
                              rounded-md bg-gray-400 px-1.5 py-1
                              text-xs text-white
                              opacity-0 transition-opacity
                              group-hover:opacity-100
                            "
                            >
                              View
                            </span>
                          </div>

                          {updatesAllowed &&
                            !isWarehouseViewOnly &&
                            !hasOpenDispute &&
                            row.status.toLowerCase() !== "received" && (
                              <div className="relative group">
                                <button
                                  id={`warehouse-inward-row-${rowId}-action-record`}
                                  onClick={(e) => {
                                    e.stopPropagation();

                                    const path =
                                      row?.poType === "Infra PO"
                                        ? `${urls.warehouse_inward}/receive/${row?.type}/${row?.poType}/${row?.inward_order_id}`
                                        : `${urls.warehouse_inward}/receive/${row?.type}/${row?.inward_order_id}`;

                                    navigate.push(path);
                                  }}
                                  className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-yellow-500"
                                >
                                  <Icon
                                    path={mdiFileCheckOutline}
                                    className="h-4 w-4 text-success group-hover:text-white"
                                  />
                                </button>

                                <span
                                  className="
                                  pointer-events-none
                                  absolute top-full left-1/2 -translate-x-1/2 mt-1
                                  rounded-md bg-gray-400 px-1.5 py-1
                                  text-xs text-white
                                  opacity-0 transition-opacity
                                  group-hover:opacity-100
                                "
                                >
                                  Record
                                </span>
                              </div>
                            )}
                        </td>
                      </tr>

                      {(timelineRows.has(row.id) ||
                        (cascadeHistory &&
                          row.inward_order_id in timelinesByEntity)) && (
                        <WorkflowTimelineRow
                          entityType={row.type === "PO" ? "PO" : "ST"}
                          entityId={row.inward_order_id}
                          colSpan={11}
                          includeInwardStages={row.type !== "PO"}
                          prefetchedEvents={
                            timelinesByEntity[row.inward_order_id]
                          }
                          deferToParent
                        />
                      )}
                      {hasItems && isExpanded && (
                        <tr className="border-b bg-gray-50">
                          <td colSpan={11} className="p-4">
                            <div className="rounded-md border border-gray-200 bg-white overflow-hidden">
                              <div className="px-4 py-2 text-sm font-semibold text-gray-700 border-b bg-gray-50">
                                Items ({items.length})
                              </div>
                              <table className="w-full text-sm">
                                <thead className="bg-white border-b">
                                  <tr>
                                    <th className="px-4 py-2 text-left font-semibold text-gray-600">
                                      Item Name
                                    </th>
                                    <th className="px-4 py-2 text-left font-semibold text-gray-600">
                                      Item Code
                                    </th>
                                    <th className="px-4 py-2 text-right font-semibold text-gray-600">
                                      Ordered
                                    </th>
                                    <th className="px-4 py-2 text-right font-semibold text-gray-600">
                                      Received
                                    </th>
                                    <th className="px-4 py-2 text-right font-semibold text-gray-600">
                                      Pending
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {items.map((it, idx) => {
                                    const ordered = it.requestedQty ?? 0;
                                    const received = it.receivedQty ?? 0;
                                    const pending = Math.max(
                                      ordered - received,
                                      0,
                                    );
                                    const matched = isItemMatch(it);
                                    const cellBg = matched
                                      ? "bg-green-100"
                                      : "";
                                    return (
                                      <tr
                                        key={`${row.id}-item-${idx}`}
                                        className={`border-b last:border-b-0 ${
                                          matched
                                            ? "bg-green-100 border-l-4 border-l-green-500"
                                            : ""
                                        }`}
                                      >
                                        <td
                                          className={`px-4 py-2 text-gray-800 ${cellBg}`}
                                        >
                                          {it.itemName || "--"}
                                        </td>
                                        <td
                                          className={`px-4 py-2 text-gray-500 ${cellBg}`}
                                        >
                                          {it.inventoryItemCode || "--"}
                                        </td>
                                        <td
                                          className={`px-4 py-2 text-right font-semibold text-gray-800 ${cellBg}`}
                                        >
                                          {ordered}
                                        </td>
                                        <td
                                          className={`px-4 py-2 text-right font-semibold text-green-600 ${cellBg}`}
                                        >
                                          {received}
                                        </td>
                                        <td
                                          className={`px-4 py-2 text-right font-semibold ${cellBg} ${
                                            pending > 0
                                              ? "text-amber-600"
                                              : "text-gray-400"
                                          }`}
                                        >
                                          {pending}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
            </tbody>
          </table>
        </div>

        <div className="md:hidden p-4 pt-0 space-y-3">
          {inwardList &&
            inwardList.length > 0 &&
            inwardList.map((row: WarehouseInwardModel) => {
              const statusKey = row?.status.toLowerCase();
              const statusClass = statusClassMap[statusKey] ?? "";

              const isRevised =
                row.inward_order_id?.toLowerCase()?.startsWith("po-") &&
                row.inward_order_id?.split("-")?.length > 2;

              const openDisputeSummary = disputeSummary[
                row.inward_order_id
              ] ?? {
                hasOpenDispute: false,
                quarantinedCount: 0,
              };
              const hasOpenDispute = openDisputeSummary.hasOpenDispute;
              const quarantinedCount = openDisputeSummary.quarantinedCount;

              const items = row.items || [];
              const hasItems = items.length > 0;
              const hasMatchedItem = items.some(isItemMatch);
              const isExpanded =
                expandedRows.has(row.id) || hasMatchedItem || cascade;
              const showTimeline =
                timelineRows.has(row.id) ||
                (cascadeHistory && row.inward_order_id in timelinesByEntity);
              const rowId = row.inward_order_id ?? row.id;

              return (
                <MobileCard
                  key={row.id}
                  id={`warehouse-inward-card-${rowId}`}
                  onClick={() => handleRowClick(row)}
                  className={isRevised ? "border-l-4 border-l-primary" : ""}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 break-words">
                        {row?.inward_order_id ?? "--"}
                      </p>
                      <div className="mt-1 flex flex-wrap items-center gap-1">
                        {isRevised && (
                          <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold gap-1 status-revised">
                            <Icon path={mdiHistory} size={0.5} />
                            Revise
                          </span>
                        )}
                        {row.is_disputed && (
                          <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold gap-1 status-rejected">
                            <Icon path={mdiBlockHelper} size={0.4} />
                            GRN Blocked
                          </span>
                        )}
                        {hasOpenDispute && (
                          <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold gap-1 status-disputed">
                            <Icon path={mdiAlertOutline} size={0.5} />
                            Disputed
                          </span>
                        )}
                        {quarantinedCount > 0 && (
                          <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold gap-1 status-pending">
                            <Icon path={mdiAlertOutline} size={0.5} />
                            {quarantinedCount} quarantined
                          </span>
                        )}
                      </div>
                    </div>
                    <span
                      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold shrink-0 ${statusClass}`}
                    >
                      {row.status}
                    </span>
                  </div>

                  <div className="divide-y divide-gray-100">
                    <MobileField label="Type">
                      <span className="flex flex-col items-end gap-1">
                        {row?.type}
                        {row?.st_subtype &&
                          row?.type === "ST" &&
                          ["Replacement", "Returnable"].includes(
                            row?.st_subtype,
                          ) && (
                            <span
                              className={`inline-flex items-center rounded-full border gap-1 font-medium text-xs px-1 py-0.5 ${
                                row.st_subtype === "Replacement"
                                  ? "bg-yellow-100 text-yellow-600 border-yellow-300"
                                  : "bg-blue-100 text-blue-600 border-blue-300"
                              }`}
                            >
                              <Icon
                                path={
                                  row.st_subtype === "Replacement"
                                    ? mdiSwapHorizontal
                                    : mdiRotateLeft
                                }
                                size={0.5}
                              />
                              {row.st_subtype}
                            </span>
                          )}
                      </span>
                    </MobileField>
                    <MobileField label="Created By">
                      {row?.created_by_name}
                    </MobileField>
                    <MobileField label="Created At">
                      {formatDateYYYYMMDD(row?.created_at)}
                    </MobileField>
                    <MobileField label="From">{row?.from?.name}</MobileField>
                    <MobileField label="To Warehouse">
                      {row.toWarehouse?.name}
                    </MobileField>
                    <MobileField label="Expected">
                      {formatDateYYYYMMDD(row?.expectedDate)}
                    </MobileField>
                    <MobileField label="Value">
                      <span className="inline-flex flex-col items-end">
                        <span>₹ {formatAmountWithCommas(row?.totalPrice)}</span>
                        <CurrencyBadge
                          currency={row?.currency}
                          rate={row?.vendor_currency_to_inr}
                          inrAmount={row?.totalPrice}
                        />
                      </span>
                    </MobileField>
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-2 border-t pt-3">
                    <button
                      id={`warehouse-inward-card-${rowId}-action-view`}
                      onClick={(e) => {
                        e.stopPropagation();
                        openViewModal(row);
                      }}
                      className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-100"
                    >
                      <Icon path={mdiEyeOutline} size={0.6} />
                      View
                    </button>
                    {updatesAllowed &&
                      !isWarehouseViewOnly &&
                      !hasOpenDispute &&
                      row.status.toLowerCase() !== "received" && (
                        <button
                          id={`warehouse-inward-card-${rowId}-action-record`}
                          onClick={(e) => {
                            e.stopPropagation();
                            const path =
                              row?.poType === "Infra PO"
                                ? `${urls.warehouse_inward}/receive/${row?.type}/${row?.poType}/${row?.inward_order_id}`
                                : `${urls.warehouse_inward}/receive/${row?.type}/${row?.inward_order_id}`;
                            navigate.push(path);
                          }}
                          className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs text-success hover:bg-gray-100"
                        >
                          <Icon path={mdiFileCheckOutline} size={0.6} />
                          Record
                        </button>
                      )}
                    <button
                      id={`warehouse-inward-card-${rowId}-timeline`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleTimeline(row.id);
                      }}
                      className={`inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs hover:bg-gray-100 ${
                        timelineRows.has(row.id)
                          ? "text-blue-600"
                          : "text-gray-700"
                      }`}
                    >
                      <History className="h-3.5 w-3.5" />
                      Timeline
                    </button>
                    {hasItems && (
                      <button
                        id={`warehouse-inward-card-${rowId}-expand`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpanded(row.id);
                        }}
                        className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-100"
                      >
                        <Icon
                          path={isExpanded ? mdiChevronDown : mdiChevronRight}
                          size={0.6}
                        />
                        Items ({items.length})
                      </button>
                    )}
                  </div>

                  {hasItems && isExpanded && (
                    <div
                      className="mt-2 rounded-md border border-gray-200 bg-white overflow-hidden"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="px-3 py-2 text-xs font-semibold text-gray-700 border-b bg-gray-50">
                        Items ({items.length})
                      </div>
                      <div className="divide-y">
                        {items.map((it, idx) => {
                          const ordered = it.requestedQty ?? 0;
                          const received = it.receivedQty ?? 0;
                          const pending = Math.max(ordered - received, 0);
                          const matched = isItemMatch(it);
                          return (
                            <div
                              key={`${row.id}-m-item-${idx}`}
                              className={`px-3 py-2 text-sm ${
                                matched
                                  ? "bg-green-100 border-l-4 border-l-green-500"
                                  : ""
                              }`}
                            >
                              <p className="font-medium text-gray-800 break-words">
                                {it.itemName || "--"}
                              </p>
                              <p className="text-xs text-gray-500 break-words">
                                {it.inventoryItemCode || "--"}
                              </p>
                              <div className="mt-1 flex gap-4 text-xs">
                                <span className="text-gray-800 font-semibold">
                                  Ordered: {ordered}
                                </span>
                                <span className="text-green-600 font-semibold">
                                  Received: {received}
                                </span>
                                <span
                                  className={`font-semibold ${
                                    pending > 0
                                      ? "text-amber-600"
                                      : "text-gray-400"
                                  }`}
                                >
                                  Pending: {pending}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {showTimeline && (
                    <div onClick={(e) => e.stopPropagation()}>
                      <table className="w-full">
                        <tbody>
                          <WorkflowTimelineRow
                            entityType={row.type === "PO" ? "PO" : "ST"}
                            entityId={row.inward_order_id}
                            colSpan={1}
                            includeInwardStages={row.type !== "PO"}
                            prefetchedEvents={
                              timelinesByEntity[row.inward_order_id]
                            }
                            deferToParent
                          />
                        </tbody>
                      </table>
                    </div>
                  )}
                </MobileCard>
              );
            })}
        </div>
      </div>

      {isViewModalOpen && selectedInward && (
        <InwardViewModal
          isOpen={isViewModalOpen}
          onClose={closeModals}
          inward_order={selectedInward}
        />
      )}
    </>
  );
};

export default InwardOrdersTable;
